/**
 * A note box that knows who is on the team.
 *
 * WHY THIS EXISTS. Free text plus fuzzy matching still fails silently at the
 * moment it matters: you type a colleague's name, it is not quite the name the
 * system knows, and you find out later - or never. A picker makes the mention
 * a CHOICE from a real list, so the common case cannot miss at all, and the
 * matching underneath only has to catch the people who type straight past it.
 *
 * IT INSERTS THE FORM THAT CANNOT BE MISREAD. Picking a person writes
 * @"Their Name" when the name contains a space, and @firstname when it does
 * not. The quoted form exists precisely because a space cannot otherwise be
 * told from the end of a mention, and extractMentions on the server reads
 * quoted names first for the same reason.
 *
 * TYPING IS STILL ALLOWED. The list is a help, not a gate: somebody who types
 * @damon and never opens the dropdown gets the same result they always would,
 * and the server now tells them if it reached nobody.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Member {
  uid: string;
  email: string;
  displayName?: string | null;
  disabled?: boolean;
}

/** What gets inserted for a person, in the form the parser reads back. */
export function mentionToken(m: Member): string {
  const name = String(m.displayName ?? '').trim();
  if (name && /\s/.test(name)) return `@"${name}"`;
  if (name) return '@' + name;
  return '@' + String(m.email ?? '').split('@')[0];
}

export function MentionBox({
  orgId, value, onChange, placeholder, rows = 2, disabled,
}: {
  orgId: string | null;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}) {
  const [team, setTeam] = useState<Member[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(-1);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!orgId) return;
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'orgTeam')({ orgId });
        const list = (r.data as { members?: Member[] }).members ?? [];
        setTeam(list.filter((m) => m.disabled !== true && m.email));
      } catch {
        // No picker rather than no note box. Typing still works.
      }
    })();
  }, [orgId]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = team;
    if (!q) return all.slice(0, 6);
    return all
      .filter((m) => {
        const name = String(m.displayName ?? '').toLowerCase();
        const email = String(m.email ?? '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 6);
  }, [team, query]);

  /**
   * Find an @ being typed at the caret.
   *
   * Only counts when the @ starts a word - an email address in the middle of a
   * sentence is not somebody starting a mention, and popping a list open over
   * their typing would be worse than not having one.
   */
  function readContext(el: HTMLTextAreaElement) {
    const caret = el.selectionStart ?? 0;
    const before = el.value.slice(0, caret);
    const idx = before.lastIndexOf('@');
    if (idx < 0) { setOpen(false); return; }
    const prev = idx === 0 ? ' ' : before[idx - 1]!;
    if (!/\s/.test(prev)) { setOpen(false); return; }
    const typed = before.slice(idx + 1);
    // A space ends it, unless we are inside a quoted name the picker inserted.
    if (/\s/.test(typed) && !typed.startsWith('"')) { setOpen(false); return; }
    setAt(idx);
    setQuery(typed.replace(/^"/, ''));
    setOpen(true);
  }

  function choose(m: Member) {
    const el = ref.current;
    if (!el || at < 0) return;
    const caret = el.selectionStart ?? value.length;
    const next = value.slice(0, at) + mentionToken(m) + ' ' + value.slice(caret);
    onChange(next);
    setOpen(false);
    // Put the caret after what was inserted, so typing continues naturally
    // rather than jumping to the end of the note.
    const pos = at + mentionToken(m).length + 1;
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(pos, pos); });
  }

  return (
    <div className="mention-box">
      <textarea
        ref={ref}
        className="field-input"
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); readContext(e.target); }}
        onKeyUp={(e) => readContext(e.currentTarget)}
        onClick={(e) => readContext(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); }
          // Enter picks the first match while the list is open, which is what
          // every other @ box in every other product does.
          if (e.key === 'Enter' && open && matches.length) { e.preventDefault(); choose(matches[0]!); }
        }}
        // Closing on blur would fire before the click on a suggestion lands.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />

      {open && (
        <div className="mention-list">
          {matches.length === 0 ? (
            <div className="mention-empty">
              Nobody on the team matches “{query}”. Typing a name that matches nobody sends
              nothing — the note will say so when you save it.
            </div>
          ) : (
            matches.map((m) => (
              <button
                type="button"
                key={m.uid}
                className="mention-option"
                // onMouseDown, not onClick: blur fires first otherwise and the
                // list is gone before the click arrives.
                onMouseDown={(e) => { e.preventDefault(); choose(m); }}
              >
                <span className="mention-name">{m.displayName || m.email}</span>
                {m.displayName && <span className="mention-email">{m.email}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
